import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { TextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { bookOpen_01Line } from "@tql/ui-components-react/untitled/icons";

import type { SelectedAddressBookItems } from "~/features/quote/context/quote-address-context.tsx";
import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import type { SubGroupKey } from "~/shared/types/quote-details-types";

import styles from "./address-row.module.css";

type AddressRowProps = {
	type: SubGroupKey;
	hasFieldError: (field: string) => boolean;
	onClearFieldError: (field: string) => void;
	onToggleAddressBookModal: () => void;
};

export function AddressRow({
	type,
	hasFieldError,
	onClearFieldError,
	onToggleAddressBookModal,
}: Readonly<AddressRowProps>) {
	const ADDRESS_MAX_LENGTH = 50;
	const { selectedAddressBookItems, updateSelectedAddressBookItem } =
		useQuoteAddressContext();
	const addressBookItem =
		selectedAddressBookItems[type as keyof SelectedAddressBookItems];
	const address1 = addressBookItem?.address1 ?? "";
	const isAddress1OverLimit = address1.length > ADDRESS_MAX_LENGTH;
	const showAddress1Error =
		isAddress1OverLimit || hasFieldError(`${type}Address1`);
	const address1ErrorMessage = isAddress1OverLimit
		? "Cannot exceed character limit."
		: "Required";
	const address2 = addressBookItem?.address2 ?? "";
	const isAddress2OverLimit = address2.length > ADDRESS_MAX_LENGTH;
	const showAddress2Error =
		isAddress2OverLimit || hasFieldError(`${type}Address2`);
	const address2ErrorMessage = isAddress2OverLimit
		? "Cannot exceed character limit."
		: undefined;
	return (
		<>
			<div className={"pickup-delivery-row"}>
				<div className={`pickup-delivery-address ${styles.addressWrapper}`}>
					<Label editorId={`${type}Address1`}>
						Address 1 <span className={"required"}>*</span>
					</Label>
					<TextBox
						id={`${type}Address1`}
						name={`${type}Address1`}
						onChange={(e) => {
							onClearFieldError(`${type}Address1`);
							onClearFieldError(`${type}Address1Length`);
							updateSelectedAddressBookItem(
								type,
								"address1",
								e.target.value as string,
							);
						}}
						valid={!showAddress1Error}
						value={address1}
					/>
					<div className={styles.addressRowErrorsWrapper}>
						{showAddress1Error ? (
							<Error>{address1ErrorMessage}</Error>
						) : (
							<span />
						)}
						{address1.length > 0 ? (
							<span
								className={`${styles.charCounter} ${
									isAddress1OverLimit ? styles.charCounterOverLimit : ""
								}`}
							>
								{address1.length}/{ADDRESS_MAX_LENGTH}
							</span>
						) : null}
					</div>
				</div>
				<div className={"pickup-delivery-address-book-button"}>
					<Button
						onClick={onToggleAddressBookModal}
						svgIcon={bookOpen_01Line}
						tabIndex={-1}
					>
						Address Book
					</Button>
				</div>
			</div>
			<div className={"pickup-delivery-row"}>
				<div className={`pickup-delivery-address ${styles.addressWrapper}`}>
					<Label editorId={`${type}Address2`}>Address 2</Label>
					<TextBox
						id={`${type}Address2`}
						name={`${type}Address2`}
						onChange={(e) => {
							onClearFieldError(`${type}Address2Length`);
							updateSelectedAddressBookItem(
								type,
								"address2",
								e.target.value as string,
							);
						}}
						valid={!showAddress2Error}
						value={address2}
					/>
					<div className={styles.addressRowErrorsWrapper}>
						{showAddress2Error ? (
							<Error>{address2ErrorMessage}</Error>
						) : (
							<span />
						)}
						{address2.length > 0 ? (
							<span
								className={`${styles.charCounter} ${
									isAddress2OverLimit ? styles.charCounterOverLimit : ""
								}`}
							>
								{address2.length}/{ADDRESS_MAX_LENGTH}
							</span>
						) : null}
					</div>
				</div>
			</div>
		</>
	);
}
